import { RwCurrency } from '@components/atoms';
import { RwLoading } from '@components/RwLoading';
import { RwEmpty } from '@components/RwEmpty';
import { t } from '@services/i18n';
import { useApiQuery } from '@hooks/useApiQuery';
import type { Rule, LevelEconomy } from '@shared/types';
import type { MemberScreenProps } from './types';

/**
 * How to earn — a plain-language reference of every published reward rule
 * (activity / reward / limit) plus a levels grid derived from lifetime
 * Reputation. Reads live from the site `rules` endpoint (published-only rules
 * + levels). Shows a calm empty state when there is nothing yet.
 */
const TIER_DOT_COLORS = [
  'var(--rw-fg-3)',
  'oklch(0.6 0.12 150)',
  'var(--rw-accent)',
  'oklch(0.55 0.13 270)',
  'oklch(0.58 0.16 330)',
];

interface HowToEarnResponse {
  rules: Rule[];
  levels: LevelEconomy[];
}

export function UserHowToEarn(_props: MemberScreenProps) {
  const { data, isLoading } = useApiQuery<HowToEarnResponse>(['rw-rules'], 'rules', {
    fallback: { rules: [], levels: [] },
  });
  if (isLoading) return <RwLoading variant="page" />;
  const pub = data?.rules ?? [];
  const levels = data?.levels ?? [];

  return (
    <div className="rw-page rw-user">
      <header className="rw-page-h">
        <div>
          <div className="rw-eyebrow">{t('COM_REWARDIFY_UHOWTO_REFERENCE', 'Reference')}</div>
          <h1 className="rw-h1">{t('COM_REWARDIFY_UHOWTO_TITLE', 'How to earn')}</h1>
          <p className="rw-lede">
            {t(
              'COM_REWARDIFY_UHOWTO_LEDE',
              "Every meaningful action is rewarded. Here's exactly what each is worth and any limits, in plain language.",
            )}
          </p>
        </div>
      </header>

      <section className="rw-card">
        {pub.length === 0 ? (
          <RwEmpty
            title={t('COM_REWARDIFY_UHOWTO_RULES_EMPTY_TITLE', 'No reward rules yet')}
            hint={t(
              'COM_REWARDIFY_UHOWTO_RULES_EMPTY_HINT',
              'Once published reward rules exist, the actions you can earn from will appear here.',
            )}
          />
        ) : (
          <table className="rw-table">
            <thead>
              <tr>
                <th>{t('COM_REWARDIFY_UHOWTO_COL_ACTIVITY', 'Activity')}</th>
                <th>{t('COM_REWARDIFY_UHOWTO_COL_REWARD', 'Reward')}</th>
                <th>{t('COM_REWARDIFY_UHOWTO_COL_LIMIT', 'Limit')}</th>
              </tr>
            </thead>
            <tbody>
              {pub.map((r) => (
              <tr key={r.id}>
                <td style={{ fontWeight: 500, maxWidth: 360 }}>
                  {r.name}
                  <div className="rw-fg2" style={{ fontSize: 12, fontWeight: 400, marginTop: 2 }}>
                    {r.plain}
                  </div>
                </td>
                <td>
                  {r.actions
                    .filter((a) => a.type === 'grant')
                    .map((a, i) => (
                      <div key={i}>
                        <RwCurrency id={a.currency ?? ''} amount={a.amount ?? 0} />
                      </div>
                    ))}
                  {r.actions.some((a) => a.type === 'grant_badge') && (
                    <div className="rw-pill rw-pill-badge" style={{ marginTop: 4 }}>
                      {t('COM_REWARDIFY_UHOWTO_PLUS_BADGE', '+ badge')}
                    </div>
                  )}
                </td>
                <td className="rw-fg2" style={{ fontSize: 12 }}>
                  {r.limits?.perUserPerDay
                    ? t('COM_REWARDIFY_UHOWTO_PER_DAY', '%s/day', r.limits.perUserPerDay)
                    : r.limits?.perUserLifetime
                      ? t('COM_REWARDIFY_UHOWTO_ONCE_EVER', 'once ever')
                      : r.limits?.perObject || t('COM_REWARDIFY_UHOWTO_UNLIMITED', 'unlimited')}
                </td>
              </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <h3 className="rw-section-t" style={{ marginTop: 24 }}>
        {t('COM_REWARDIFY_UHOWTO_LEVELS', 'Levels')}
      </h3>
      {levels.length === 0 ? (
        <RwEmpty
          title={t('COM_REWARDIFY_UHOWTO_LEVELS_EMPTY_TITLE', 'No levels yet')}
          hint={t(
            'COM_REWARDIFY_UHOWTO_LEVELS_EMPTY_HINT',
            'Reputation levels will appear here once they are configured.',
          )}
        />
      ) : (
        <div className="rw-tier-grid">
          {levels.map((l, i) => (
          <div key={l.id} className="rw-tier-card">
            <div className="rw-tier-dot" style={{ background: TIER_DOT_COLORS[i] }} />
            <div style={{ fontWeight: 600, fontSize: 16, fontFamily: 'var(--rw-display)' }}>{l.name}</div>
            <div className="rw-fg2 rw-mono" style={{ fontSize: 12 }}>
              {l.min.toLocaleString()}
              {l.max === null ? '+' : `-${l.max.toLocaleString()}`} {t('COM_REWARDIFY_UHOWTO_REP', 'REP')}
            </div>
            <div className="rw-fg1" style={{ fontSize: 12, marginTop: 4 }}>
              {l.perk}
            </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
